import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Plus, Settings, Play, UserMinus, AlertTriangle, ChevronDown, ChevronUp, ListChecks } from 'lucide-react';
import BulkRegistrationDialog from '../registrations/BulkRegistrationDialog';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const RegistrationsTab = ({
  competitions,
  freedivers,
  eventTypes,
  groupedRegistrations,
  onCreateEventSession,
  onRefresh,
  t
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [form, setForm] = useState({
    competition_id: '',
    freediver_id: '',
    event_type_code: '',
    announced_performance: ''
  });
  // Track collapsed/expanded state per event group — default collapsed
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const initial = {};
    if (groupedRegistrations) {
      Object.keys(groupedRegistrations).forEach(key => { initial[key] = true; });
    }
    return initial;
  });
  // Keep new groups collapsed by default
  React.useEffect(() => {
    if (groupedRegistrations) {
      setCollapsedGroups(prev => {
        const updated = { ...prev };
        Object.keys(groupedRegistrations).forEach(key => {
          if (!(key in updated)) updated[key] = true;
        });
        return updated;
      });
    }
  }, [groupedRegistrations]);
  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  const translate = (key, fallback) => t ? t(key) : fallback;

  const isSTAEvent = (code) => code === 'STA';

  const getEventTypeUnit = (code) => {
    if (isSTAEvent(code)) return 'mm:ss';
    const eventType = eventTypes.find(et => et.code === code);
    return eventType?.scoring_rule === 'time' ? 'seconds' : 'meters';
  };

  const formatSTADisplay = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const totalSec = Math.round(Number(seconds));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Parse mm:ss to seconds
  const parseMMSS = (val) => {
    if (!val) return 0;
    const parts = val.split(':');
    if (parts.length === 2) {
      const mins = parseInt(parts[0], 10) || 0;
      const secs = parseInt(parts[1], 10) || 0;
      return mins * 60 + secs;
    }
    return parseFloat(val) || 0;
  };

  const getEventTypeName = (code) => {
    const eventType = eventTypes.find(et => et.code === code);
    return eventType?.name || code;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const apValue = isSTAEvent(form.event_type_code)
        ? parseMMSS(form.announced_performance)
        : parseFloat(form.announced_performance);
      await axios.post(`${API_BASE_URL}/api/registrations`, {
        ...form,
        announced_performance: apValue
      });
      setShowModal(false);
      setForm({ competition_id: '', freediver_id: '', event_type_code: '', announced_performance: '' });
      onRefresh();
    } catch (error) {
      console.error('Error creating registration:', error);
      alert('Error creating registration. Please try again.');
    }
  };

  const handleUnregister = async (registrationId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/registrations/${registrationId}`);
      onRefresh();
    } catch (error) {
      console.error('Error unregistering:', error);
      alert(translate('errorUnregistering', 'Error unregistering. Please try again.'));
    }
  };

  // State for unregister confirmation
  const [unregisterConfirm, setUnregisterConfirm] = useState({ show: false, registration: null });

  const confirmUnregister = async () => {
    if (!unregisterConfirm.registration) return;
    await handleUnregister(unregisterConfirm.registration.id);
    setUnregisterConfirm({ show: false, registration: null });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-blue-200">{translate('eventRegistrations', 'Event Registrations')}</h2>
        <div className="flex gap-2">
          <Button
            onClick={() => {
              onRefresh();
              setShowBulkModal(true);
            }}
            className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white border-none"
            data-testid="bulk-registration-btn"
          >
            <ListChecks className="mr-2" size={18} />
            {translate('bulkRegistration', 'Inscriptions multiples')}
          </Button>
          <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogTrigger asChild>
            <Button 
              variant="outline"
              className="border-blue-500/50 text-blue-200 hover:bg-blue-800/40"
              onClick={() => onRefresh()}
              data-testid="new-registration-btn"
            >
              <Plus className="mr-2" size={18} />
              {translate('singleRegistration', 'Inscription rapide')}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-blue-200">{translate('createRegistration', 'Create Event Registration')}</DialogTitle>
              <DialogDescription className="text-blue-300">
                {translate('createRegistrationDesc', 'Register a freediver for a specific event in a competition.')}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label htmlFor="competition_id" className="text-blue-200">{translate('competition', 'Competition')}</Label>
                <Select value={form.competition_id} onValueChange={(value) => setForm({...form, competition_id: value})}>
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                    <SelectValue placeholder={translate('selectCompetition', 'Select competition')} />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    {competitions
                      .filter((comp) => !['completed', 'closed', 'cancelled'].includes((comp.status || '').toLowerCase()))
                      .map((comp) => (
                      <SelectItem key={comp.id} value={comp.id}>{comp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="freediver_id" className="text-blue-200">{translate('freediver', 'Freediver')}</Label>
                <Select value={form.freediver_id} onValueChange={(value) => setForm({...form, freediver_id: value})}>
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                    <SelectValue placeholder={translate('selectFreediver', 'Select freediver')} />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    {freedivers.map((freediver) => (
                      <SelectItem key={freediver.id} value={freediver.id}>
                        {freediver.first_name} {freediver.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="event_type_code" className="text-blue-200">{translate('eventType', 'Event Type')}</Label>
                <Select value={form.event_type_code} onValueChange={(value) => setForm({...form, event_type_code: value})}>
                  <SelectTrigger className="bg-blue-800/50 border-blue-600 text-white">
                    <SelectValue placeholder={translate('selectEventType', 'Select event type')} />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600">
                    {eventTypes.map((eventType) => (
                      <SelectItem key={eventType.id} value={eventType.code}>
                        {eventType.code} - {eventType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="announced_performance" className="text-blue-200">
                  {translate('announcedPerformance', 'Announced Performance')} ({getEventTypeUnit(form.event_type_code)})
                </Label>
                {isSTAEvent(form.event_type_code) ? (
                  <Input
                    id="announced_performance"
                    type="text"
                    value={form.announced_performance}
                    onChange={(e) => {
                      let val = e.target.value.replace(/[^0-9:]/g, '');
                      // Auto-insert colon after 2 digits
                      if (val.length === 2 && !val.includes(':') && form.announced_performance.length < val.length) {
                        val = val + ':';
                      }
                      if (val.length > 5) val = val.slice(0, 5);
                      setForm({...form, announced_performance: val});
                    }}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="mm:ss (e.g. 04:30)"
                    required
                    data-testid="announced-performance-sta-input"
                  />
                ) : (
                  <Input
                    id="announced_performance"
                    type="number"
                    step="0.1"
                    value={form.announced_performance}
                    onChange={(e) => setForm({...form, announced_performance: e.target.value})}
                    className="bg-blue-800/50 border-blue-600 text-white"
                    placeholder="e.g., 75.5"
                    required
                    data-testid="announced-performance-input"
                  />
                )}
              </div>
              <DialogFooter>
                <Button type="submit" className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700" data-testid="submit-registration-btn">
                  {translate('createRegistration', 'Create Registration')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Bulk registration dialog */}
      <BulkRegistrationDialog
        open={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        competitions={competitions}
        freedivers={freedivers}
        eventTypes={eventTypes}
        onSaved={onRefresh}
        translate={translate}
      />

      {/* Registrations grouped by event */}
      <div className="space-y-6">
        {Object.keys(groupedRegistrations).length > 0 ? (
          Object.entries(groupedRegistrations).map(([key, eventData]) => (
            <Card key={key} className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-teal-200 text-lg mb-1">
                      {eventData.competition.name}
                    </CardTitle>
                    <CardTitle className="text-blue-200 text-xl">
                      {eventData.event_type_code} - {eventData.event_type.name}
                    </CardTitle>
                    <p className="text-blue-300 text-sm mt-1">{eventData.event_type.description}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge variant="secondary" className="bg-teal-700/50 text-teal-200">
                      {eventData.registrations.length} {translate('registered', 'registered')}
                    </Badge>
                    <Button
                      onClick={() => toggleGroup(key)}
                      size="sm"
                      variant="outline"
                      className="border-blue-500 text-blue-300 hover:bg-blue-700/30"
                      data-testid={`toggle-registrations-${key}`}
                    >
                      {collapsedGroups[key] ? <ChevronDown size={16} className="mr-1" /> : <ChevronUp size={16} className="mr-1" />}
                      {collapsedGroups[key] ? (translate('showList', 'Show List')) : (translate('hideList', 'Hide List'))}
                    </Button>
                    <Button
                      onClick={() => onCreateEventSession(eventData.competition_id, eventData.event_type_code)}
                      className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white"
                      data-testid={`start-live-event-${key}`}
                    >
                      <Play className="mr-2" size={16} />
                      {translate('startLiveEvent', 'Start Live Event')}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!collapsedGroups[key] && (
                <div className="space-y-3">
                  {eventData.registrations
                    .sort((a, b) => a.announced_performance - b.announced_performance)
                    .map((registration, index) => (
                    <div key={registration.id} className="flex items-center justify-between p-4 bg-blue-900/30 rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-white font-semibold">
                            {registration.freediver?.first_name} {registration.freediver?.last_name}
                          </p>
                          <div className="flex items-center space-x-3 text-sm text-blue-300">
                            <span><strong>{translate('nationality', 'Nationality')}:</strong> {registration.freediver?.nationality}</span>
                            <span><strong>{translate('sex', 'Gender')}:</strong> {registration.freediver?.gender}</span>
                            <span><strong>{translate('eventLabel', 'Event')}:</strong> {getEventTypeName(registration.event_type_code)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-teal-300 font-semibold">
                            PA: {isSTAEvent(registration.event_type_code)
                              ? formatSTADisplay(registration.announced_performance)
                              : `${registration.announced_performance} ${getEventTypeUnit(registration.event_type_code)}`}
                          </p>
                          <p className="text-blue-400 text-sm">
                            {eventData.event_type.scoring_rule}
                          </p>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                onClick={() => setUnregisterConfirm({ show: true, registration })}
                                size="sm"
                                variant="outline"
                                className="border-red-500 text-red-300 hover:bg-red-700/50"
                                data-testid={`unregister-${registration.id}`}
                              >
                                <UserMinus size={16} />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-red-900 border-red-700 text-white">
                              <p>{translate('unregister', 'Unregister')} {registration.freediver?.first_name} {registration.freediver?.last_name}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm">
            <CardContent className="text-center py-12">
              <Settings size={48} className="mx-auto text-blue-400 mb-4" />
              <h3 className="text-xl font-semibold text-blue-200 mb-2">{translate('noRegistrationsYet', 'No Registrations Yet')}</h3>
              <p className="text-blue-300">{translate('startByRegistering', 'Start by registering freedivers for events in competitions.')}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Unregister Confirmation Dialog */}
      <AlertDialog open={unregisterConfirm.show} onOpenChange={(open) => !open && setUnregisterConfirm({ show: false, registration: null })}>
        <AlertDialogContent className="bg-gradient-to-br from-red-900/95 to-slate-900/95 border-red-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-200 flex items-center">
              <AlertTriangle className="mr-2 text-red-400" size={20} />
              {translate('confirmUnregister', 'Confirm Unregistration')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              {translate('confirmUnregisterMessage', 'Are you sure you want to unregister this freediver from the event?')}
              {unregisterConfirm.registration && (
                <div className="mt-3 p-3 bg-red-900/30 rounded-lg border border-red-700/50">
                  <p className="text-white font-semibold">
                    {unregisterConfirm.registration.freediver?.first_name} {unregisterConfirm.registration.freediver?.last_name}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {unregisterConfirm.registration.event_type_code} - AP: {unregisterConfirm.registration.announced_performance}
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white border-slate-600 hover:bg-slate-600">
              {translate('cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmUnregister}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {translate('unregister', 'Unregister')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RegistrationsTab;
